import { css } from '@emotion/react';

import { isTaskStateFilter } from 'types/RunState';
import { statePriority, taskStateColor } from 'utils/taskStateColors';

const terminalTaskStates: ReadonlySet<string> = new Set(['FAILED', 'COMPLETED', 'CANCELLED', 'ARCHIVED', 'DISCARDED']);

const chipLayout = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

function StateCount({
  state,
  count,
  selected,
  onSelect,
}: {
  state: string;
  count: number;
  selected: boolean;
  onSelect?: (state: string) => void;
}) {
  const color = taskStateColor(state);
  const inner = (
    <>
      <span
        aria-hidden
        css={css`
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: ${color};
        `}
      />
      <span>
        {state}{' '}
        <span
          css={css`
            font-weight: 600;
          `}
        >
          {count.toLocaleString('en-US')}
        </span>
      </span>
    </>
  );

  if (!onSelect || !isTaskStateFilter(state)) {
    return <span css={chipLayout}>{inner}</span>;
  }

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`Filter workflows with ${state} tasks`}
      title={selected ? `Clear ${state} task filter` : `Filter workflows with ${state} tasks`}
      onClick={() => onSelect(state)}
      css={css`
        ${chipLayout};
        appearance: none;
        margin: 0;
        padding: 0 2px;
        border: none;
        border-radius: 4px;
        background: ${selected ? `color-mix(in srgb, ${color} 22%, transparent)` : 'transparent'};
        box-shadow: ${selected ? `inset 0 0 0 1px ${color}` : 'none'};
        color: inherit;
        font: inherit;
        cursor: pointer;

        &:hover {
          background: color-mix(in srgb, ${color} 16%, transparent);
        }
      `}
    >
      {inner}
    </button>
  );
}

export default function TaskCountBreakdown({
  counts,
  selectedState = '',
  onSelectState,
}: {
  counts: Record<string, number>;
  selectedState?: string;
  onSelectState?: (state: string) => void;
}) {
  const entries = Object.entries(counts)
    .filter(([state, count]) => count > 0 && !terminalTaskStates.has(state))
    .sort(([a], [b]) => statePriority(a) - statePriority(b));

  if (entries.length === 0) {
    return null;
  }

  return (
    <ul
      aria-label="Task counts by state"
      css={css`
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        align-items: baseline;
        gap: 0 12px;
        margin: 0 0 0 auto;
        padding: 0;
        list-style: none;
        font-size: 0.875rem;
        line-height: 1.6;
        font-variant-numeric: tabular-nums;
        color: rgba(0, 0, 0, 0.7);

        li {
          display: flex;
        }
      `}
    >
      {entries.map(([state, count]) => (
        <li key={state}>
          <StateCount state={state} count={count} selected={selectedState === state} onSelect={onSelectState} />
        </li>
      ))}
    </ul>
  );
}
